import { activateConfig, defaultConfig, readSession, writeSession } from '../config';
import { addBookingDays, parseBookingDate, validateBookingDates } from './bookingDates';
import { safeClientUrl } from './bookingUrl';

export const bookingSessionKey = (config, hotelId) => `bookingSession:${config.shop_code}:${config.shop_id}:${hotelId || 'none'}`;

export function createBookingEntry(property, entry) {
  const { config, hotels } = property;
  const widget = entry.query.get('from_widget') === 'true';
  const previousShopId = readSession('shop_id');
  const previousShopCode = readSession('shop_code');
  // Older default-shop logins did not store identity keys. Keep those sessions
  // only for the configured default; an explicit different shop still signs out.
  const previousConfig = !previousShopId && !previousShopCode
    ? defaultConfig : { shop_id: previousShopId, shop_code: previousShopCode };
  const sameShop = String(previousConfig.shop_id) === String(config.shop_id) &&
    previousConfig.shop_code?.toLowerCase() === config.shop_code?.toLowerCase();
  let legacy = null;
  try { legacy = sameShop ? JSON.parse(readSession('bookingSession')) : null; } catch { /* Ignore old corrupt data. */ }
  let hotel = property.hotel;
  if ((property.legacy || property.kind === 'shop') && !widget && !entry.query.has('hotel_id') && legacy?.searchState?.hotelId) {
    hotel = hotels.find(item => String(item.id) === String(legacy.searchState.hotelId)) || hotel;
  }
  const key = bookingSessionKey(config, hotel?.id);
  let saved = null;
  try {
    const scoped = JSON.parse(readSession(key));
    if (scoped?.version === 1 && Date.now() - scoped.savedAt < 86400000) saved = scoped.state;
  } catch { /* Ignore expired or corrupt state. */ }
  if (!saved && sameShop && String(legacy?.searchState?.hotelId) === String(hotel?.id)) saved = legacy;

  let dates = { checkin: new Date(), checkout: addBookingDays(new Date(), 2) };
  if (saved?.searchState) {
    try {
      dates = validateBookingDates(new Date(saved.searchState.checkin), new Date(saved.searchState.checkout));
    } catch { saved = null; }
  }
  const start = entry.query.get('start_date');
  const end = entry.query.get('end_date');
  if (start || end) {
    if (!start) throw new Error('A check-in date is required when a check-out date is provided.');
    const checkin = parseBookingDate(start);
    dates = validateBookingDates(checkin, end ? parseBookingDate(end) : checkin && addBookingDays(checkin, 2));
  }
  const roomInput = entry.query.get('room_data');
  let rooms = saved?.rooms || [{ adults: 2, children: 0, childAges: [] }];
  if (roomInput) {
    if (roomInput.length > 20000) throw new Error('The room allocation in this link is too large.');
    let parsed;
    try {
      try { parsed = JSON.parse(roomInput); }
      catch { parsed = JSON.parse(decodeURIComponent(roomInput)); } // Old widgets double-encode JSON.
    } catch { throw new Error('The room and guest information in this link is invalid.'); }
    rooms = parsed;
  } else if (widget) rooms = [{ adults: 2, children: 0, childAges: [] }];
  try {
    rooms = normalizeRooms(rooms);
  } catch (error) {
    if (roomInput || widget) throw error;
    saved = null;
    rooms = [{ adults: 2, children: 0, childAges: [] }];
  }
  const guests = rooms.reduce((sum, room) => sum + room.adults + room.children, 0);
  if ((entry.query.has('rooms') && Number(entry.query.get('rooms')) !== rooms.length) ||
      (entry.query.has('guests') && Number(entry.query.get('guests')) !== guests)) {
    throw new Error('The room and guest totals do not match the room allocation.');
  }

  if (!sameShop) {
    ['userToken', 'userData', 'refresh_token', 'bookingSession', 'bookingInfo'].forEach(name => writeSession(name, null));
  }
  // Do not restore another hotel's branding or widget flags.
  ['hotel_logo', 'hotel_name', 'hotel_cancel_policy', 'widgetData', 'widgetProcessed', 'isWidgetInitialized', 'shouldAutoSearch'].forEach(name => writeSession(name, null));
  const clientUrl = safeClientUrl(entry.query.get('client_url') || (sameShop && saved?.searchState?.clientUrl));
  writeSession('client_url', clientUrl);
  writeSession('hotel_id', hotel?.id || null);
  activateConfig(config);
  const sameSearch = saved?.searchState &&
    String(saved.searchState.hotelId) === String(hotel?.id) &&
    new Date(saved.searchState.checkin).toDateString() === dates.checkin.toDateString() &&
    new Date(saved.searchState.checkout).toDateString() === dates.checkout.toDateString() &&
    JSON.stringify(saved.rooms) === JSON.stringify(rooms) &&
    (saved.searchState.promo_code || '') === (entry.query.get('promo_code') || saved.searchState.promo_code || '');
  const explicitSearch = widget || !sameSearch;
  const restoreCart = !entry.query.has('payment_status') && !explicitSearch && saved?.cart?.length;
  return {
    config, hotels, sessionKey: key,
    initialState: {
      ...(restoreCart ? saved : {}),
      searchState: {
        ...(saved?.searchState || {}),
        hotel: hotel?.name || '', hotelId: hotel?.id,
        ...dates, shop_id: config.shop_id,
        guestsText: `${rooms.length} Room${rooms.length === 1 ? '' : 's'}, ${guests} Guest${guests === 1 ? '' : 's'}`,
        guestCount: guests,
        room_guest: rooms.map((room, index) => ({ room_no: index + 1, no_of_adults: room.adults, no_of_children: room.children, child_ages: room.childAges })),
        clientUrl, searchPerformed: false, apiResponse: undefined,
        promo_code: entry.query.get('promo_code') || (widget ? null : saved?.searchState?.promo_code) || null,
        promo_from_widget: widget ? !!entry.query.get('promo_code') : !!saved?.searchState?.promo_from_widget,
      },
      hotelData: null,
      rooms, cart: restoreCart ? saved.cart : [],
      hasDiscount: restoreCart ? !!saved.hasDiscount : false,
      subtotal: restoreCart ? saved.subtotal : 0, taxes: restoreCart ? saved.taxes : 0,
      total: restoreCart ? saved.total : 0, finalTotal: restoreCart ? saved.finalTotal : 0,
      rateTotal: restoreCart ? saved.rateTotal : 0, offerDiscount: restoreCart ? saved.offerDiscount : 0,
      user: sameShop ? saved?.user || null : null,
    },
  };
}

function normalizeRooms(rooms) {
  if (!Array.isArray(rooms) || !rooms.length || rooms.length > 20) throw new Error('Please select between 1 and 20 rooms.');
  return rooms.map(room => {
    const adults = Number(room.adults ?? room.no_of_adults);
    const children = Number(room.children ?? room.no_of_children ?? 0);
    if (!Number.isInteger(adults) || adults < 1 || adults > 20 || !Number.isInteger(children) || children < 0 || children > 20) {
      throw new Error('The guest allocation in this link is invalid.');
    }
    const childAges = room.childAges || room.child_ages || Array(children).fill(5);
    if (!Array.isArray(childAges) || childAges.length !== children || childAges.some(age => !Number.isInteger(Number(age)) || age < 0 || age > 17)) {
      throw new Error('Please provide a valid age for each child.');
    }
    return { adults, children, childAges: childAges.map(Number) };
  });
}
